import { isUnlimited } from '@cartwhl/common/stores/userStore';
import { getHomepageUrl } from '@cartwhl/common/utils';
import ProBadge from '@cartwhl/ui/atoms/ProBadge';
import Text from '@cartwhl/ui/base/Text';
import Button from '@cartwhl/ui/input/Button';
import Modal from '@cartwhl/ui/molecules/Modal';

type SpinsExplainedModalProps =
  | {
      open: boolean;
      onOpenChange: (open: boolean) => void;
      close?: never;
    }
  | {
      close: () => void;
      open?: never;
      onOpenChange?: never;
    };

export default function SpinsExplainedModal(props: SpinsExplainedModalProps) {
  const open = 'open' in props ? props.open : true;

  function handleOpenChange(next: boolean) {
    if (typeof props.onOpenChange === 'function') {
      props.onOpenChange(next);
      return;
    }
    if (!next) {
      props.close();
    }
  }

  function handleClose() {
    handleOpenChange(false);
  }

  return (
    <Modal
      title="What are Spins?"
      maxWidth={684}
      open={open}
      onOpenChange={handleOpenChange}
    >
      <Text as="p" variant="studio/body/md">
        Spins are a legacy credit system used on older Cartwheel plans to
        generate motion and download assets. We&apos;ve moved to monthly credits
        on our new Individual and Team plans, which include more usage,
        workspace features, and better value. If you&apos;re still on Spins,
        the breakdown below shows what each action costs today.
      </Text>
      <Text
        as="p"
        variant="studio/body/md"
        className="text-gray-60 dark:text-gray-45 mt-4"
      >
        Ready for more? Upgrade to a current plan to unlock monthly credits and
        everything Cartwheel offers today.
      </Text>
      <div className="mb-2 mt-6">
        <Text as="span" variant="studio/title/md">
          Legacy spin costs
        </Text>
      </div>
      <div className="relative rounded-lg border p-6">
        <SpinCostRow
          title="Pose rendering"
          description="Rendering a pose will cost 10 Spins."
          cost="10 Spins"
        />
        <SpinCostRow
          title="Text to motion"
          description="Generating motion from a text prompt will cost 25 Spins."
          cost="25 Spins"
        />
        <SpinCostRow
          title="Generate character"
          description="Generating a new character will cost 130 Spins."
          cost="130 Spins"
        />
        <SpinCostRow
          title="CopyCat"
          description="Generating motion from a video stream or upload will cost 50 Spins."
          cost="50 Spins"
        />
        <div className="flex w-full flex-row flex-nowrap items-start gap-4">
          <div>
            <Text
              as="span"
              variant="studio/body/medium/md"
              className="text-gray-90 dark:text-white"
            >
              Export
            </Text>
            <Text
              as="p"
              variant="studio/body/md"
              className="max-w-[374px] py-1 opacity-60"
            >
              Exporting motion will vary depending on the desired filetype. See
              the list below for the filetype and spin cost breakdown.
            </Text>
            <ul className="marker:text-gray-60 mt-4 list-disc space-y-2 pl-5">
              <li className="cw-text-box-none leading-normal">
                <Text as="span" variant="studio/body/md" className="opacity-60">
                  Image or Video →{' '}
                </Text>
                <Text as="span" variant="studio/body/bold/md">
                  0 Spins
                </Text>
              </li>
              <li className="cw-text-box-none leading-normal">
                <Text as="span" variant="studio/body/md" className="opacity-60">
                  BVH, FBX, glTF →{' '}
                </Text>
                <Text as="span" variant="studio/body/bold/md">
                  50 Spins
                </Text>
              </li>
              <li className="cw-text-box-none leading-normal">
                <Text as="span" variant="studio/body/md" className="opacity-60">
                  Maya →{' '}
                </Text>
                <Text as="span" variant="studio/body/bold/md">
                  100 Spins
                </Text>
                <ProBadge className="ml-1 align-middle" />
              </li>
            </ul>
          </div>
          <Text as="div" variant="studio/title/md" className="ml-auto">
            0 - 100 Spins
          </Text>
        </div>
      </div>
      <div className="mt-6 flex flex-row flex-nowrap">
        <Button variant="secondary" onClick={handleClose}>
          Close
        </Button>
        {!isUnlimited() && (
          <Button
            className="ml-auto"
            variant="primary"
            onClick={() => {
              window.location.href = getHomepageUrl('/pricing');
            }}
          >
            Upgrade
          </Button>
        )}
      </div>
    </Modal>
  );
}

function SpinCostRow({
  title,
  description,
  cost,
}: {
  title: string;
  description: string;
  cost: string;
}) {
  return (
    <div className="mb-8 flex w-full flex-row flex-nowrap items-start gap-4 border-b pb-4">
      <div>
        <Text
          as="span"
          variant="studio/body/medium/md"
          className="text-gray-90 dark:text-white"
        >
          {title}
        </Text>
        <Text
          as="p"
          variant="studio/body/md"
          className="max-w-[374px] py-1 opacity-60"
        >
          {description}
        </Text>
      </div>
      <Text as="div" variant="studio/title/md" className="ml-auto">
        {cost}
      </Text>
    </div>
  );
}
